import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The results half of Geometry.
 *
 * Each of these is chosen because it is genuinely surprising the first time:
 * that similar triangles make trigonometry possible at all; that every angle
 * standing on the same arc is the same size; that a formula for distance falls
 * out of a theorem about triangles; that a ratio can be run backwards to
 * measure a tower nobody can climb.
 */
export const GEOMETRY_HS_LESSONS: Lesson[] = [
  lesson({
    id: 'triangle-properties-01',
    unitId: 'unit-triangles',
    conceptId: 'triangle-properties',
    title: 'Properties of Triangles',
    summary: 'What follows from congruence: isosceles triangles, the exterior angle, and which side lengths are possible at all.',
    objectives: [
      'Use the equal base angles of an isosceles triangle',
      'Apply the exterior angle theorem',
      'Decide whether three lengths can form a triangle',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Results that congruence buys',
        blocks: [
          text('Congruence was worth establishing because of what it lets you deduce. Three results follow almost immediately, and all three are used constantly.'),
          list([
            'An **isosceles** triangle — two equal sides — has two equal angles, opposite those sides.',
            'An **exterior angle** equals the sum of the two interior angles it does not touch.',
            'Any two sides together must be longer than the third, or no triangle exists.',
          ]),
        ],
      },
      intuition: {
        title: 'Where each comes from',
        blocks: [
          text('**Isosceles.** Draw the line from the apex to the midpoint of the base. It splits the triangle into two triangles sharing that line, with two equal sides each — SSS. So they are congruent, and the base angles correspond.'),
          insight('The equal angles are opposite the equal sides. That pairing is what you actually use: seeing two equal sides tells you which *angles* match, and vice versa.'),
          text('**Exterior angle.** Extend one side. The exterior angle and the interior angle beside it make a straight line, so they add to 180°; the three interior angles also add to 180°.'),
          math('e = 180 - c = a + b'),
          insight('So the exterior angle equals the two *remote* interior angles added together — a shortcut that saves a step in a great many problems.'),
          text('**The triangle inequality.** Two sides of lengths 3 and 4 cannot reach across a gap of 10; swung down from either end, they never meet.'),
          warning('Check the two shortest against the longest. 3, 4, 7 fails by exactly nothing — 3 + 4 = 7 gives a flat line, not a triangle — so the inequality has to be strict.', 'The borderline case'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'equal base angles', height: 'equal sides' } },
            'An isosceles triangle. The two equal angles sit opposite the two equal sides.',
          ),
          table(['Sides', 'Check', 'Triangle?'], [
            ['3, 4, 5', '3 + 4 = 7 > 5', 'yes'],
            ['2, 2, 3', '2 + 2 = 4 > 3', 'yes'],
            ['3, 4, 7', '3 + 4 = 7, not more', 'no — flat'],
            ['1, 2, 9', '1 + 2 = 3 < 9', 'no'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('An isosceles triangle has an apex angle of 40°. Find a base angle, and then the exterior angle at a base vertex.'),
          steps([
            { label: 'The base angles are equal and share what is left.', latex: '(180 - 40) \\div 2 = 70°' },
            { label: 'The exterior angle is the sum of the two remote interior angles.', latex: '40 + 70 = 110°' },
            { label: 'Check against the straight line.', latex: '180 - 70 = 110 \;\\checkmark' },
          ]),
          text('Both routes to the exterior angle agree, which is the check worth doing — the remote-angle shortcut and the straight line must always give the same number.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('triangle-properties', 4)] },
      independent: { title: 'On your own', blocks: [practice('triangle-properties', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('triangle-properties', 3, 'Which side lengths are possible at all.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Equal sides face equal angles, and the reverse.',
            'An exterior angle equals the two remote interior angles added.',
            'Any two sides must total strictly more than the third.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'similarity-01',
    unitId: 'unit-triangles',
    conceptId: 'similarity',
    title: 'Similar Triangles',
    summary: 'Same shape, different size — and the reason a scale drawing tells you anything at all.',
    objectives: [
      'Use AA to establish similarity',
      'Find a missing side with a scale factor',
      'Scale areas and volumes correctly',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Same shape, different size',
        blocks: [
          text('Two figures are **similar** when they have the same shape: equal angles, and sides in the same ratio throughout.'),
          text('Congruence needed three measurements. Similarity needs fewer, because size no longer has to match.'),
          insight('**AA is enough.** If two angles of one triangle equal two of another, the third must too — they all add to 180° — and the triangles are similar. Two facts, and the shape is fixed.'),
        ],
      },
      intuition: {
        title: 'The scale factor, and what it does to area',
        blocks: [
          text('Similar triangles have every pair of corresponding sides in the same ratio, and that ratio is the **scale factor**.'),
          math('\\frac{a\'}{a} = \\frac{b\'}{b} = \\frac{c\'}{c} = k'),
          text('Finding a missing side is then the proportional reasoning from Grade 7: find k from a pair you know, and apply it.'),
          warning('Check the direction. Going from the small figure to the large one multiplies; the other way divides. The answer should come out larger or smaller accordingly, and glancing at it catches the error immediately.', 'Which way round'),
          text('Now the part that surprises almost everyone. Double every length of a shape. What happens to its area?'),
          insight('It **quadruples**, not doubles. The enlargement stretches the shape in two directions at once, so area scales by k². Volume, stretched in three directions, scales by k³.'),
          table(['Scale factor', 'Lengths', 'Areas', 'Volumes'], [
            ['2', '× 2', '× 4', '× 8'],
            ['3', '× 3', '× 9', '× 27'],
            ['½', '× 0.5', '× 0.25', '× 0.125'],
            ['k', '× k', '× k²', '× k³'],
          ]),
          tip('This is why a scale model of a building cannot be made of the same materials: at one-tenth scale it has a hundredth of the surface but a thousandth of the weight, and nothing behaves the way the original does.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'area-model', rows: 4, columns: 4, shadedRows: 2, shadedColumns: 2 },
            'The shaded square has half the side of the whole, and a quarter of the area. Halving a length quarters an area.',
          ),
          text('Similar triangles also appear whenever a line is drawn parallel to one side of a triangle — the small triangle it cuts off has the same angles as the whole, by corresponding angles. That single observation solves a great many problems.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Two similar triangles have corresponding sides of 6 cm and 15 cm. The smaller has another side of 8 cm and an area of 24 cm². Find the matching side and the area of the larger.'),
          steps([
            { label: 'Find the scale factor.', latex: 'k = 15 \\div 6 = 2.5' },
            { label: 'Apply it to the side.', latex: '8 \\times 2.5 = 20\\text{ cm}' },
            { label: 'Area scales by the square.', latex: 'k^2 = 6.25' },
            { label: 'Apply that to the area.', latex: '24 \\times 6.25 = 150\\text{ cm}^2' },
          ]),
          text('Note the second scale factor is not 2.5 but 6.25. Using 2.5 on the area would have given 60 — a plausible-looking number that is wrong by a factor of two and a half.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('similarity', 4)] },
      independent: { title: 'On your own', blocks: [practice('similarity', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('similarity', 3, 'Scaling areas, where the factor is squared.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Similar means equal angles and sides in a constant ratio.',
            'AA is enough to establish similarity.',
            'Check which way the scale factor goes.',
            'Areas scale by k², volumes by k³.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'triangle-proportionality-01',
    unitId: 'unit-triangles',
    conceptId: 'triangle-proportionality',
    title: 'Proportionality in Triangles',
    summary: 'A line parallel to one side cuts the other two in the same ratio — and the two results that follow from saying so carefully.',
    objectives: [
      'Use the side-splitter theorem to find a missing length',
      'Apply the midsegment theorem',
      'Use the angle bisector theorem',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Cutting a triangle with a parallel line',
        blocks: [
          text('Draw any triangle. Now draw a line across it parallel to one of the sides. It meets the other two sides somewhere, and cuts each of them into two pieces.'),
          text('The question is whether those two cuts have anything to do with each other. They do, and the reason is already in your hands: the small triangle at the top has the same angles as the whole one, by corresponding angles, so the two are similar.'),
          insight('Similarity says corresponding sides are in a constant ratio. Applied here it says the parallel line cuts both sides in the *same* ratio — whichever two pieces you compare.'),
        ],
      },
      intuition: {
        title: 'One theorem, and two special cases worth naming',
        blocks: [
          text('**The side-splitter theorem.** A line parallel to one side of a triangle divides the other two sides proportionally.'),
          math('\\frac{a}{b} = \\frac{c}{d}'),
          text('Here a and b are the two pieces of one side, c and d the two pieces of the other, taken in the same order — top piece with top piece, bottom with bottom.'),
          warning('The commonest error is comparing a piece with a whole side. Either write part over part on both sides of the equation, or whole over whole on both. Mixing them compares two different measurements and gives a number that looks plausible and is wrong.', 'Part with part, or whole with whole'),
          text('**The midsegment theorem** is the case where the parallel line hits both midpoints. Join the midpoints of two sides of a triangle and the segment you get is parallel to the third side — and exactly half its length.'),
          math('\\text{midsegment} = \\tfrac{1}{2} \\times \\text{third side}'),
          insight('The halving is not an extra fact. The small triangle is the whole one at a scale factor of one half, so every one of its sides is half the matching side, the third side included.'),
          text('**The angle bisector theorem** is the same shape of result for a different line. The bisector of an angle of a triangle cuts the opposite side in the ratio of the two sides that make the angle.'),
          math('\\frac{BD}{DC} = \\frac{AB}{AC}'),
          tip('Read that as "the bisector divides the far side in the same proportion as the two sides it sits between". A bisector from the apex of an isosceles triangle therefore lands exactly at the midpoint — which is the symmetry you would have expected anyway, and a useful check that you have the ratio the right way up.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: '12', height: '6', hypotenuse: 'DE parallel to BC' } },
            'DE is parallel to BC, so triangle ADE is similar to triangle ABC and the two sides are cut in the same ratio.',
          ),
          table(['Given', 'What it lets you write'], [
            ['A line parallel to one side', 'part ÷ part equal on both sides'],
            ['A line joining two midpoints', 'parallel to the third side, half its length'],
            ['A bisector of one angle', 'far side split in the ratio of the two near sides'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In triangle ABC, D lies on AB and E on AC, with DE parallel to BC. AD = 6, DB = 4 and AE = 9. Find EC.'),
          steps([
            { label: 'DE is parallel to BC, so the two sides are cut in the same ratio.', latex: '\\frac{AD}{DB} = \\frac{AE}{EC}' },
            { label: 'Substitute what is known.', latex: '\\frac{6}{4} = \\frac{9}{EC}' },
            { label: 'Cross-multiply.', latex: '6 \\times EC = 36' },
            { label: 'Divide.', latex: 'EC = 6' },
          ]),
          text('Note which lengths were paired. AD went with AE — the two pieces nearer A — and DB with EC. Pairing AD with EC instead would have given 2.67, a number the working would never flag as wrong.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('triangle-proportionality', 4)] },
      independent: { title: 'On your own', blocks: [practice('triangle-proportionality', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('triangle-proportionality', 3, 'Bisectors, and proportions where a whole side is given rather than a piece.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A line parallel to one side of a triangle cuts the other two in the same ratio.',
            'Keep the proportion consistent: part with part, or whole with whole.',
            'A segment joining two midpoints is parallel to the third side and half its length.',
            'An angle bisector splits the opposite side in the ratio of the two sides forming the angle.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polygons-01',
    unitId: 'unit-polygons-circles',
    conceptId: 'polygons',
    title: 'Polygons and Quadrilaterals',
    summary: 'Angle sums for any number of sides, and what makes a parallelogram a parallelogram.',
    objectives: [
      'Find the interior angle sum of any polygon',
      'Use the fact that exterior angles always total 360°',
      'Identify quadrilaterals by their defining properties',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'From triangles to any number of sides',
        blocks: [
          text('A triangle holds 180°. What about a pentagon, or a shape with twenty sides?'),
          text('There is no need for a new result. Pick a corner and draw diagonals to every other: the polygon falls apart into triangles, and each holds 180°.'),
          math('\\text{interior sum} = (n - 2) \\times 180°'),
        ],
      },
      intuition: {
        title: 'Two totals, and only one of them changes',
        blocks: [
          text('A polygon with n sides splits into n − 2 triangles — always two fewer, because the two sides at the chosen corner do not produce one.'),
          table(['Sides', 'Triangles', 'Interior sum'], [
            ['3', '1', '180°'],
            ['4', '2', '360°'],
            ['5', '3', '540°'],
            ['6', '4', '720°'],
            ['12', '10', '1800°'],
          ]),
          insight('The **exterior** angles behave quite differently: they add to 360° for every polygon, whatever its number of sides. Walk once around the outside and you have turned through exactly one full revolution — and that is true for a triangle and a hundred-gon alike.'),
          warning('These two facts get swapped constantly. The interior sum grows with the number of sides; the exterior sum never changes. If your answer is 360° for a pentagon, you have used the wrong one.', 'Which total?'),
          text('For a *regular* polygon the exterior fact is the more useful of the two, because it gives each angle in one step:'),
          math('\\text{one exterior angle} = \\frac{360°}{n}, \\qquad \\text{one interior angle} = 180° - \\frac{360°}{n}'),
          text('Quadrilaterals then get names from their properties, and the names nest: every square is a rectangle *and* a rhombus, every rectangle is a parallelogram.'),
          table(['Shape', 'Defining property'], [
            ['Trapezium', 'one pair of parallel sides'],
            ['Parallelogram', 'both pairs of opposite sides parallel'],
            ['Rhombus', 'a parallelogram with four equal sides'],
            ['Rectangle', 'a parallelogram with four right angles'],
            ['Square', 'both a rhombus and a rectangle'],
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'polygon', sides: 6, showCentre: true, label: 'a hexagon splits into 4 triangles' },
            'Diagonals from one corner cut a hexagon into four triangles, so its angles total 4 × 180 = 720°.',
          ),
          figure(
            { kind: 'polygon', sides: 8, label: 'regular octagon' },
            'A regular octagon: each exterior angle is 360 ÷ 8 = 45°, so each interior angle is 135°.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find each interior angle of a regular polygon with 10 sides, two ways.'),
          steps([
            { label: 'Via the exterior angle.', latex: '360 \\div 10 = 36°' },
            { label: 'Interior and exterior make a straight line.', latex: '180 - 36 = 144°' },
            { label: 'Or via the interior sum.', latex: '(10 - 2) \\times 180 = 1440°' },
            { label: 'Shared between ten equal corners.', latex: '1440 \\div 10 = 144°' },
          ]),
          tip('The first route is shorter and less error-prone, which is why the exterior angle is worth reaching for first even when the question asks about interior ones.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polygons', 4)] },
      independent: { title: 'On your own', blocks: [practice('polygons', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('polygons', 3, 'Identifying quadrilaterals from their properties.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Interior angles total (n − 2) × 180°.',
            'Exterior angles total 360°, for every polygon.',
            'For a regular polygon, one exterior angle is 360 ÷ n.',
            'Quadrilateral names nest: every square is also a rectangle and a rhombus.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'quadrilateral-properties-01',
    unitId: 'unit-polygons-circles',
    conceptId: 'quadrilateral-properties',
    title: 'Quadrilaterals and Their Diagonals',
    summary: 'What each name in the family actually guarantees, and the diagonals that tell one from another.',
    objectives: [
      'State the properties of a parallelogram and prove one of them',
      'Distinguish rectangle, rhombus and square by their diagonals',
      'Use the properties of trapeziums and kites',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A name is a promise',
        blocks: [
          text('You already know which quadrilateral is which by its sides and angles. This lesson is about what each name then *entitles* you to — because in a proof, naming a shape is how you import several facts at once.'),
          text('And every one of those facts is a theorem. "Opposite sides of a parallelogram are equal" is not part of the definition; the definition says only that they are parallel. Equality has to be argued.'),
          insight('Draw one diagonal of a parallelogram and it cuts the shape into two triangles. Alternate angles give two pairs of equal angles, the diagonal is common to both, and ASA finishes it: the triangles are congruent, so the opposite sides match. That is the whole proof, and every other property of a parallelogram is found the same way.'),
        ],
      },
      intuition: {
        title: 'The family, and what each member adds',
        blocks: [
          text('A **parallelogram** has both pairs of opposite sides parallel. From that alone follows everything in the first row below.'),
          table(['Shape', 'Sides', 'Angles', 'Diagonals'], [
            ['Parallelogram', 'opposite sides equal', 'opposite angles equal; consecutive add to 180°', 'bisect each other'],
            ['Rectangle', 'opposite sides equal', 'all four are 90°', 'bisect each other **and are equal**'],
            ['Rhombus', 'all four equal', 'opposite angles equal', 'bisect each other **at right angles**, and bisect the angles'],
            ['Square', 'all four equal', 'all four are 90°', 'equal **and** perpendicular'],
            ['Kite', 'two adjacent pairs equal', 'one pair of opposite angles equal', 'perpendicular; one bisects the other'],
            ['Trapezium', 'one pair parallel', 'each pair on a leg adds to 180°', 'no general property'],
          ]),
          insight('The diagonals are the useful column, because they are what tells the three parallelograms apart. **Equal** diagonals mean a rectangle. **Perpendicular** diagonals mean a rhombus. Both at once means a square, and no fourth possibility exists.'),
          warning('Equal and perpendicular are separate guarantees and get swapped constantly. A rhombus can be drawn long and thin, with one diagonal far longer than the other; a rectangle can be drawn with diagonals crossing at any angle you like except a right one. Only the square manages both.', 'Equal is not perpendicular'),
          text('A **kite** is the one that breaks the pattern: its equal sides are *adjacent* rather than opposite, so nothing in it has to be parallel and it is not a parallelogram at all. What it does have is one axis of symmetry — the long diagonal — which is why exactly one pair of opposite angles is equal and why the diagonals meet at 90°.'),
          text('For a **trapezium**, the segment joining the midpoints of the two non-parallel sides — its midsegment — is parallel to both parallel sides, and its length is their average:'),
          math('m = \\frac{a + b}{2}'),
          tip('An *isosceles* trapezium, whose two legs are equal, adds a pair of equal base angles and equal diagonals — the trapezium closest to being a rectangle without being one.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'symmetry', shape: 'parallelogram', showLines: true, label: 'parallelogram: no mirror line' }),
          figure({ kind: 'symmetry', shape: 'rectangle', showLines: true, label: 'rectangle: two mirror lines' }),
          figure({ kind: 'symmetry', shape: 'kite', showLines: true, label: 'kite: one mirror line, along a diagonal' }),
          text('The mirror lines say the same thing the diagonal column does. A parallelogram has none, which is why so few of its parts are forced to match; a kite has one, running along a diagonal, which is exactly the pair of angles that turn out to be equal.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('The diagonals of a quadrilateral bisect each other, are equal in length, and cross at 68°. What is the shape? And separately, a trapezium has parallel sides of 14 cm and 22 cm — how long is its midsegment?'),
          steps([
            { label: 'Diagonals bisecting each other make it a parallelogram.', latex: '\\text{parallelogram}' },
            { label: 'Equal diagonals narrow that to a rectangle.', latex: '\\text{rectangle}' },
            { label: 'They do not cross at 90°, so it is not a rhombus — and therefore not a square.', latex: '68° \\ne 90°' },
            { label: 'For the trapezium, average the parallel sides.', latex: 'm = \\frac{14 + 22}{2} = 18\\text{ cm}' },
          ]),
          text('The 68° was not a distraction: it is what rules out the square. Each fact about the diagonals removes exactly one candidate, which is why they are worth holding as a set rather than one at a time.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('quadrilateral-properties', 4)] },
      independent: { title: 'On your own', blocks: [practice('quadrilateral-properties', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('quadrilateral-properties', 3, 'Identifying a shape from its diagonals alone.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Opposite sides and angles of a parallelogram are equal, and its diagonals bisect each other — all of it provable from the definition.',
            'Equal diagonals mean a rectangle; perpendicular diagonals mean a rhombus; both mean a square.',
            'A kite has adjacent equal sides, one mirror line and perpendicular diagonals — and is not a parallelogram.',
            'The midsegment of a trapezium is the average of the two parallel sides.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'circle-theorems-01',
    unitId: 'unit-polygons-circles',
    conceptId: 'circle-theorems',
    title: 'Circle Theorems',
    summary: 'Angles, chords and tangents — where several of the most surprising results in elementary geometry live.',
    objectives: [
      'Use the relationship between a central angle and an inscribed angle',
      'Apply the right angle between a tangent and a radius',
      'Recognise the angle in a semicircle',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A shape with unusual symmetry',
        blocks: [
          text('A circle has every symmetry a plane figure can have, and that produces results with no analogue elsewhere.'),
          text('Here is one to be surprised by. Pick two points on a circle. Now pick a third, anywhere on the major arc, and measure the angle it makes looking at the first two. Move it. The angle does not change.'),
          insight('Every point on that arc sees the chord at exactly the same angle. Nothing about the circle was special — this is true of every circle and every chord.'),
        ],
      },
      intuition: {
        title: 'Four results, and where they come from',
        blocks: [
          text('**The angle at the centre is twice the angle at the circumference**, when both stand on the same arc. This is the central one, and the others follow from it.'),
          math('\\angle \\text{centre} = 2 \\times \\angle \\text{circumference}'),
          text('The proof is short: draw the radius to the third point, and two isosceles triangles appear, because all radii are equal. The exterior angle theorem finishes it.'),
          insight('**Angles in the same segment are equal** is now immediate. Each equals half the same central angle, so they equal each other — which is the observation from a moment ago, proved.'),
          text('**The angle in a semicircle is a right angle.** A diameter subtends a straight angle at the centre, 180°, so the angle at the circumference is half of it.'),
          math('\\frac{180°}{2} = 90°'),
          insight('That is a striking special case. Take any point at all on a circle, join it to the two ends of a diameter, and you always get a right angle — never anything else.'),
          text('**A tangent meets the radius at 90°** at the point of contact. A tangent touches at exactly one point, and the shortest distance from the centre to the line is the perpendicular.'),
          warning('That right angle is usually the whole content of a tangent question, and it is easy to look past because nothing in the picture is marked. Draw the radius to the point of contact and the right angle appears.', 'The angle you have to add yourself'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'circle-diagram', elements: ['central-angle', 'inscribed-angle'] },
            'The angle at the centre and the angle at the circumference stand on the same arc. The first is twice the second.',
          ),
          figure(
            { kind: 'circle-diagram', elements: ['tangent', 'radius'] },
            'The tangent touches at one point, and the radius drawn there meets it at a right angle.',
          ),
          table(['Result', 'In short'], [
            ['Centre and circumference', 'centre = 2 × circumference'],
            ['Same segment', 'equal angles'],
            ['Semicircle', '90°'],
            ['Tangent and radius', '90°'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('An angle at the centre of a circle is 130°. Find the angle at the circumference on the same arc. Separately, a tangent meets a circle at T, and a chord from T makes 35° with the radius. What angle does the chord make with the tangent?'),
          steps([
            { label: 'Halve the central angle.', latex: '130 \\div 2 = 65°' },
            { label: 'For the tangent, the radius meets it at 90°.', latex: '90°' },
            { label: 'Subtract the given angle.', latex: '90 - 35 = 55°' },
          ]),
          text('Neither answer needed anything measured. Both came from a result that holds for every circle, which is the whole method of this course.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('circle-theorems', 4)] },
      independent: { title: 'On your own', blocks: [practice('circle-theorems', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('circle-theorems', 3, 'Tangents, where the right angle has to be spotted.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The angle at the centre is twice the angle at the circumference on the same arc.',
            'Angles in the same segment are equal.',
            'The angle in a semicircle is 90°.',
            'A tangent meets the radius at the point of contact at 90°.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'chords-secants-01',
    unitId: 'unit-polygons-circles',
    conceptId: 'chords-secants',
    title: 'Chords, Secants and Tangents',
    summary: 'Lines cutting a circle, and the product of two lengths that turns out not to depend on where they cross.',
    objectives: [
      'Use the perpendicular from the centre to a chord',
      'Apply the intersecting chords theorem',
      'Use the secant and tangent forms of the same result',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Three names for one line',
        blocks: [
          text('A line and a circle can meet in two points, one point, or none. The three cases have names, and the names matter because the theorems are stated in them.'),
          list([
            'A **chord** is the segment joining two points of the circle. A diameter is the longest chord there is.',
            'A **secant** is the whole line through those two points — a chord extended out past the circle in both directions.',
            'A **tangent** touches at exactly one point, and meets the radius drawn there at 90°.',
          ]),
          insight('The last lesson found angles. This one finds *lengths*, and one theorem covers all three cases at once — which is unusual enough to be worth expecting the punchline.'),
        ],
      },
      intuition: {
        title: 'The perpendicular that halves a chord, and the product that will not move',
        blocks: [
          text('**Start with a single chord.** Drop a perpendicular from the centre of the circle onto it. That perpendicular bisects the chord.'),
          text('The reason is congruence: joining the centre to each end of the chord makes two right triangles sharing a leg, with equal hypotenuses because both are radii. HL says they are congruent, so the two halves of the chord are equal.'),
          insight('This is what turns a chord question into Pythagoras. With a radius r, a distance d from the centre to the chord, and half the chord h, the three sit in one right triangle:'),
          math('h^2 + d^2 = r^2, \\qquad \\text{chord} = 2h'),
          warning('The triangle contains half the chord, never the whole one. Finding h and reporting it as the answer is the standard slip — double it, unless the question asked for the half.', 'Half in, whole out'),
          text('**Now let two chords cross inside the circle.** Each is cut into two pieces. The surprising fact is that the product of the two pieces of one chord equals the product of the two pieces of the other, wherever they happen to cross.'),
          math('a \\times b = c \\times d'),
          text('It comes straight from similar triangles: the two triangles formed at the crossing have equal angles, because angles in the same segment are equal, so their sides are in proportion — and cross-multiplying that proportion is the result.'),
          text('**Move the crossing point outside the circle** and the same argument runs again with two secants. Now each length is measured from the external point, and the two lengths multiplied are the *near* intersection and the *far* one — that is, the outside piece and the whole secant.'),
          math('PA \\times PB = PC \\times PD'),
          warning('Here the second length is the whole secant, from the external point right through to the far side. Using the chord inside the circle instead of the whole secant is the commonest error in this topic, and it gives an answer that is always too small.', 'Outside piece × whole secant'),
          text('**Finally slide one secant until it becomes a tangent.** Its two intersection points have merged into one, so both lengths are the tangent length t, and the product is t × t:'),
          math('t^2 = PC \\times PD'),
          tip('All three statements are the same theorem. A point has a fixed "power" with respect to a circle, and every line you draw through that point reports the same number back — which is why the product does not care where the line goes.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'circle-diagram', elements: ['chord', 'radius'] },
            'The perpendicular from the centre to a chord bisects it, leaving a right triangle whose sides are half the chord, the distance to the centre and a radius.',
          ),
          figure(
            { kind: 'circle-diagram', elements: ['tangent', 'chord'] },
            'From an outside point, the tangent length squared equals the outside piece of any secant times the whole of that secant.',
          ),
          table(['Where the lines cross', 'What multiplies to what'], [
            ['Inside the circle', 'piece × piece = piece × piece'],
            ['Outside, two secants', 'outside × whole = outside × whole'],
            ['Outside, tangent and secant', 'tangent² = outside × whole'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A circle has radius 13 cm and a chord 4 cm from the centre. How long is the chord? Then, from a point outside a circle, a secant meets it at 4 cm and 9 cm from the point. How long is the tangent from that point?'),
          steps([
            { label: 'Half the chord, the distance and the radius make a right triangle.', latex: 'h^2 + 4^2 = 13^2' },
            { label: 'Solve for the half.', latex: 'h^2 = 169 - 16 = 153' },
            { label: 'That is half of it, so double.', latex: '\\text{chord} = 2\\sqrt{153} \\approx 24.7\\text{ cm}' },
            { label: 'For the tangent, multiply the outside piece by the whole secant.', latex: 't^2 = 4 \\times 9 = 36' },
            { label: 'Take the root.', latex: 't = 6\\text{ cm}' },
          ]),
          text('The 9 in that last part was the distance to the *far* intersection, not the chord inside the circle. Had the question given the outside piece as 4 and the chord as 9, the whole secant would have been 13 and the answer would be √52, not 6.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('chords-secants', 4)] },
      independent: { title: 'On your own', blocks: [practice('chords-secants', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('chords-secants', 3, 'Secants from an outside point, where the whole length has to be assembled first.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The perpendicular from the centre bisects a chord, leaving a right triangle with half the chord in it.',
            'Two chords crossing inside: the products of the pieces are equal.',
            'Two secants from outside: outside piece × whole secant, equal for both.',
            'A tangent is the case where the two pieces merge, so the tangent squared equals that same product.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'coordinate-geometry-01',
    unitId: 'unit-coordinate-trig',
    conceptId: 'coordinate-geometry',
    title: 'Coordinate Geometry',
    summary: 'Distance, midpoint and the equation of a circle — geometry done with algebra instead of a compass.',
    objectives: [
      'Find the distance between two points',
      'Find a midpoint',
      'Recognise the equation of a circle',
      'Use slope to test whether two lines are parallel or perpendicular',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two subjects joined',
        blocks: [
          text('Until now geometry has been done with figures and algebra with symbols. Descartes\u2019 idea was to put a grid over the plane so that every point has a name — and then every geometric question becomes an algebraic one.'),
          text('The payoff is immediate: results that need a careful construction can instead be computed.'),
        ],
      },
      intuition: {
        title: 'Both formulas are things you already know',
        blocks: [
          text('**Distance.** Take two points and draw the horizontal and vertical gaps between them. Those are the two short sides of a right triangle, and the distance is its hypotenuse.'),
          math('d = \\sqrt{(x_2 - x_1)^2 + (y_2 - y_1)^2}'),
          insight('That is not a new formula. It is the Pythagorean theorem, with the two legs written as coordinate differences.'),
          warning('Two things go missing here. Squaring the differences — the gaps themselves do not add — and taking the root at the end. An answer of 25 where 5 was wanted means the last step was skipped.', 'Square, add, root'),
          text('**Midpoint.** Halfway along means the average of the ends, in each direction separately.'),
          math('M = \\left(\\frac{x_1 + x_2}{2},\\ \\frac{y_1 + y_2}{2}\\right)'),
          warning('An average, not a difference. Subtracting gives the size of the gap, which is a perfectly good number answering a different question — and the answer should sit *between* the two points, which is a free check.', 'Add, do not subtract'),
          text('**The equation of a circle.** A circle is every point at a fixed distance from the centre. Put that sentence into the distance formula and square both sides:'),
          math('(x - a)^2 + (y - b)^2 = r^2'),
          insight('So the equation is the definition, written down. Centre (a, b), radius r — and the signs flip, exactly as they did for the vertex of a parabola.'),
          text('**Parallel and perpendicular.** Two lines are parallel when they climb at the same rate, so their slopes are equal. Perpendicular is the more interesting one:'),
          math('m_1 \\times m_2 = -1, \\qquad\\text{so}\\qquad m_2 = -\\frac{1}{m_1}'),
          text('Turning a line through 90° swaps its across and its up, and reverses one of them — which is what taking the reciprocal and changing the sign does.'),
          warning('Both moves are needed. Negating alone gives the mirror image of the line; flipping alone gives a line leaning the same way. A slope of 2/3 has a perpendicular slope of −3/2, and the check is that the two multiply to −1.', 'Flip it and negate it'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -8,
              max: 8,
              points: [
                { x: -3, y: -2, label: 'A' },
                { x: 3, y: 6, label: 'B' },
                { x: 0, y: 2, label: 'M' },
              ],
            },
            'A to B: across 6, up 8, so the distance is 10. The midpoint M is the average of the two, at (0, 2).',
          ),
          tip('Sketch the two points before calculating, even roughly. It tells you what size of answer to expect and catches a subtraction done the wrong way round.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the distance and the midpoint between (−3, −2) and (3, 6).'),
          steps([
            { label: 'The two gaps.', latex: '\\Delta x = 6,\\quad \\Delta y = 8' },
            { label: 'Square and add.', latex: '36 + 64 = 100' },
            { label: 'Take the root.', latex: 'd = 10' },
            { label: 'Midpoint: average each coordinate.', latex: 'M = \\left(\\tfrac{-3 + 3}{2},\\ \\tfrac{-2 + 6}{2}\\right) = (0, 2)' },
          ]),
          text('The midpoint sits between the two points, as it must. A 3-4-5 triangle scaled by two also explains the distance without any arithmetic at all.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('coordinate-geometry', 4)] },
      independent: { title: 'On your own', blocks: [practice('coordinate-geometry', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('coordinate-geometry', 3, 'Distances that need the root taken carefully.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Distance is the Pythagorean theorem: square the gaps, add, take the root.',
            'A midpoint is an average, so it lies between the two points.',
            'A circle of radius r about (a, b) is (x − a)² + (y − b)² = r².',
            'Parallel lines have equal slopes; perpendicular slopes multiply to −1.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'special-triangles-01',
    unitId: 'unit-coordinate-trig',
    conceptId: 'special-triangles',
    title: 'Special Right Triangles',
    summary: 'Two triangles whose sides are always in the same ratio, so their lengths need no calculation.',
    objectives: [
      'Use the 45-45-90 ratio 1 : 1 : √2',
      'Use the 30-60-90 ratio 1 : √3 : 2',
      'Recognise when a problem contains one of them',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Two shapes worth knowing by heart',
        blocks: [
          text('Two right triangles turn up constantly — in squares, in equilateral triangles, in roof pitches and set squares. Both have side ratios that never change, so their lengths can be written down rather than computed.'),
        ],
      },
      intuition: {
        title: 'Where each ratio comes from',
        blocks: [
          text('**45-45-90.** Cut a square along its diagonal. The two shorter sides are equal, and Pythagoras gives the third:'),
          math('1^2 + 1^2 = 2 \\quad\\Longrightarrow\\quad \\text{hypotenuse} = \\sqrt{2}'),
          insight('So the ratio is 1 : 1 : √2. The hypotenuse is √2 ≈ 1.414 times a shorter side — always, whatever the size of the square.'),
          text('**30-60-90.** Cut an equilateral triangle down the middle. The base is halved, the hypotenuse is a full original side, and Pythagoras gives the height:'),
          math('1^2 + h^2 = 2^2 \\quad\\Longrightarrow\\quad h = \\sqrt{3}'),
          insight('The ratio is 1 : √3 : 2. The most useful part is the simplest: **the hypotenuse is exactly twice the side opposite the 30° angle.** No root needed.'),
          warning('Match the sides to the angles, not to their positions in the picture. The shortest side always faces the smallest angle, and the hypotenuse always faces the right angle.', 'Which side is which'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Triangle', 'Ratio', 'From'], [
            ['45-45-90', '1 : 1 : √2', 'half a square'],
            ['30-60-90', '1 : √3 : 2', 'half an equilateral triangle'],
          ]),
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: '1', height: '1', hypotenuse: '√2' } },
            'Half a square. The two legs are equal and the hypotenuse is √2 times either of them.',
          ),
          tip('These are the values a calculator gives for sin 30°, cos 45° and the rest — which is why the next lesson can quote exact answers for those angles rather than decimals.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A 30-60-90 triangle has a hypotenuse of 14 cm. Find the other two sides.'),
          steps([
            { label: 'The hypotenuse is twice the shortest side.', latex: '14 \\div 2 = 7\\text{ cm}' },
            { label: 'The remaining side is √3 times the shortest.', latex: '7\\sqrt{3} \\approx 12.12\\text{ cm}' },
            { label: 'Check with Pythagoras.', latex: '7^2 + (7\\sqrt{3})^2 = 49 + 147 = 196 = 14^2' },
          ]),
          text('The check costs a moment and confirms both answers at once. Note the middle side is left as 7√3 — exact, and more useful than a decimal for anything that follows.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('special-triangles', 4)] },
      independent: { title: 'On your own', blocks: [practice('special-triangles', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('special-triangles', 3, 'Working from the hypotenuse back to the shorter sides.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '45-45-90 has sides in the ratio 1 : 1 : √2.',
            '30-60-90 has sides in the ratio 1 : √3 : 2.',
            'In a 30-60-90 the hypotenuse is exactly twice the shortest side.',
            'The shortest side faces the smallest angle.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trig-ratios-01',
    unitId: 'unit-coordinate-trig',
    conceptId: 'trig-ratios',
    title: 'Right-Triangle Trigonometry',
    summary: 'Sine, cosine and tangent — ratios that depend on an angle and nothing else.',
    objectives: [
      'Label the opposite, adjacent and hypotenuse relative to an angle',
      'Choose the right ratio from SOH-CAH-TOA',
      'Find a missing side or angle in a right triangle',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A ratio that depends only on the angle',
        blocks: [
          text('Draw a right triangle with a 30° angle. Measure the side opposite that angle and divide by the hypotenuse. You get 0.5.'),
          text('Draw a bigger one, also with a 30° angle. Divide again: 0.5. Any size at all, and always 0.5.'),
          insight('This is similarity doing the work. All right triangles with a 30° angle are similar — AA, since they share the right angle too — so corresponding sides are in a fixed ratio. That is *why* the ratio can be given a name and put in a table.'),
        ],
      },
      intuition: {
        title: 'Three ratios, and labelling before choosing',
        blocks: [
          text('Relative to a chosen angle, the three sides get names:'),
          list([
            '**Hypotenuse** — opposite the right angle. Always the longest, and it never changes whichever angle you pick.',
            '**Opposite** — across from your chosen angle.',
            '**Adjacent** — the remaining side, next to your angle.',
          ]),
          warning('Opposite and adjacent swap when you switch to the other acute angle. The hypotenuse does not. Labelling relative to the *angle in the question* is the step that prevents most errors here.', 'Label first'),
          text('Then the three ratios, remembered as SOH-CAH-TOA:'),
          math('\\sin\\theta = \\frac{O}{H} \\qquad \\cos\\theta = \\frac{A}{H} \\qquad \\tan\\theta = \\frac{O}{A}'),
          insight('The choice is mechanical once the labelling is done. Two of the three quantities are known; pick the ratio that contains exactly those two.'),
          text('Going the other way — from a ratio to an angle — uses the inverse functions:'),
          math('\\theta = \\sin^{-1}\\!\\left(\\frac{O}{H}\\right)'),
          tip('Because sine and cosine are a side over the hypotenuse, and the hypotenuse is the longest side, both are always between 0 and 1. A sine of 1.4 is not a hard problem — it is an arithmetic mistake.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'adjacent', height: 'opposite', hypotenuse: 'hypotenuse' } },
            'The labels are relative to the marked angle. Choose the other acute angle and opposite and adjacent trade places.',
          ),
          table(['Angle', 'sin', 'cos', 'tan'], [
            ['30°', '½', '√3/2', '1/√3'],
            ['45°', '√2/2', '√2/2', '1'],
            ['60°', '√3/2', '½', '√3'],
          ]),
          insight('Those are precisely the special triangles from the last lesson, read off as ratios. Nothing new has been introduced — it has been given names.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A ladder leans against a wall at 65° to the ground, with its foot 2 m from the wall. How long is the ladder?'),
          steps([
            { label: 'Label relative to the 65° angle.', latex: '\\text{adjacent} = 2,\\quad \\text{hypotenuse} = \\ell' },
            { label: 'Adjacent and hypotenuse means cosine.', latex: '\\cos 65° = \\frac{2}{\\ell}' },
            { label: 'Rearrange.', latex: '\\ell = \\frac{2}{\\cos 65°}' },
            { label: 'Evaluate.', latex: '\\approx 4.73\\text{ m}' },
          ]),
          text('Sanity-check it: the ladder is the hypotenuse, so it must be longer than 2 m, and 4.73 is. Had the ratio been inverted the answer would have come out under 1 m — a ladder shorter than its own distance from the wall, which the picture refutes at once.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-ratios', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-ratios', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('trig-ratios', 3, 'Choosing the ratio, which is most of the difficulty.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The ratios depend on the angle alone, because such triangles are all similar.',
            'Label opposite, adjacent and hypotenuse relative to the angle in the question.',
            'SOH-CAH-TOA: pick the ratio containing the two sides you know.',
            'Sine and cosine are always between 0 and 1.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'arc-sector-01',
    unitId: 'unit-polygons-circles',
    conceptId: 'arc-sector',
    title: 'Arc Length and Sector Area',
    summary: 'A slice of a circle is a fraction of the whole one, and the fraction is written on the angle.',
    objectives: [
      'Find the arc length and area of a sector from its angle and radius',
      'Recover the angle of a sector from its arc',
      'Distinguish the perimeter of a sector from its arc',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'One formula, twice',
        blocks: [
          text('Cut a pizza into six equal slices and take one. You have a sixth of the crust and a sixth of the cheese. Nobody needs a formula for that — and a sector is the same idea with the sixth written as an angle.'),
          text('A full turn is 360°. A sector of θ° is therefore θ/360 of the circle it came from, and *every* quantity belonging to the circle is cut down by exactly that fraction.'),
          math('\\text{arc} = \\frac{\\theta}{360} \\times 2\\pi r \\qquad \\text{sector area} = \\frac{\\theta}{360} \\times \\pi r^2'),
          insight('There is only one idea here, applied to two different totals. Learning these as two unrelated formulas is what makes them so easy to swap.'),
        ],
      },
      intuition: {
        title: 'Which total, and which fraction',
        blocks: [
          text('Two decisions, and both are made before any arithmetic starts.'),
          list([
            '**Length or area?** An arc is a distance you could walk along, so it comes from the circumference 2πr. A sector is a region you could paint, so it comes from the area πr². Check the units you expect to end with — cm or cm².',
            '**Which way up is the fraction?** θ/360, always. It is the part over the whole, and it must come out less than 1 for anything smaller than the full circle.',
          ]),
          warning('If your fraction comes out bigger than 1 you have written 360/θ. That would make the slice larger than the pizza.', 'A check that costs nothing'),
          text('The perimeter of a sector is where most marks are lost, and not for a mathematical reason. Run a finger round the *edge* of a slice: out along one straight side, round the curve, and back along the other straight side.'),
          math('\\text{perimeter} = \\frac{\\theta}{360} \\times 2\\pi r + 2r'),
          tip('“Arc” and “perimeter” are different questions. The arc is only the curved part; the perimeter is the whole boundary, and it includes the two radii.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'circle-sector', degrees: 120, radiusLabel: 'r', arcLabel: 'arc' },
            'A 120° sector: a third of a full turn, so a third of the circumference and a third of the area.',
          ),
          figure(
            { kind: 'circle-sector', degrees: 300, radiusLabel: 'r' },
            'A 300° sector is still a sector. Five-sixths of the circle, and the same two formulas.',
          ),
          table(['Angle', 'Fraction of the circle', 'Arc of a circle with r = 6', 'Area'], [
            ['90°', '1/4', '9.42 cm', '28.27 cm²'],
            ['120°', '1/3', '12.57 cm', '37.70 cm²'],
            ['180°', '1/2', '18.85 cm', '56.55 cm²'],
            ['360°', '1', '37.70 cm', '113.10 cm²'],
          ]),
          insight('Read the last row first. Every other row is that row multiplied by a fraction — which is the entire content of this lesson.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A sector has a radius of 9 cm and an angle of 140°. Find its arc length, its area, and its perimeter, each to two decimal places.'),
          steps([
            { label: 'Write the fraction once and reuse it.', latex: '\\frac{140}{360} = \\frac{7}{18}' },
            { label: 'Arc: that fraction of the circumference.', latex: '\\frac{7}{18} \\times 2\\pi \\times 9 = 21.99\\text{ cm}' },
            { label: 'Area: that fraction of πr².', latex: '\\frac{7}{18} \\times \\pi \\times 81 = 98.96\\text{ cm}^2' },
            { label: 'Perimeter: the arc plus the two straight edges.', latex: '21.99 + 2 \\times 9 = 39.99\\text{ cm}' },
          ]),
          text('The arc is 21.99 cm and the whole circumference is 56.55 cm. The ratio is 0.39, which is 140/360 — worth checking, because a fraction taken upside down would have produced an arc longer than the circle.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('arc-sector', 4)] },
      independent: { title: 'On your own', blocks: [practice('arc-sector', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('arc-sector', 3, 'Running it backwards, and telling a perimeter from an arc.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sector of θ° is θ/360 of its circle — of the circumference, of the area, of everything.',
            'Arc from 2πr, area from πr². Decide which by asking whether the answer is a length or an area.',
            'The perimeter of a sector is the arc plus two radii.',
            'The fraction is always less than 1 for a slice smaller than the whole.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trig-applications-01',
    unitId: 'unit-coordinate-trig',
    conceptId: 'trig-applications',
    title: 'Solving Right Triangles',
    summary: 'Running the ratios backwards to find an angle, and pointing the whole apparatus at something too tall to measure.',
    objectives: [
      'Use sin⁻¹, cos⁻¹ and tan⁻¹ to find an angle from two sides',
      'Decide whether the unknown side is multiplied or divided',
      'Model a problem with an angle of elevation or depression',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The question the ratios cannot answer yet',
        blocks: [
          text('You can find a side from an angle. The reverse question — *what angle produces this ratio?* — is the one that turns trigonometry from a calculation into a measuring instrument.'),
          text('A ramp rises 1 m over a run of 6 m. Is it too steep for a wheelchair? The ratio is easy: 1/6 ≈ 0.167. The building code is written in degrees.'),
          math('\\tan\\theta = \\frac{1}{6} \\quad\\Longrightarrow\\quad \\theta = \\tan^{-1}\\!\\left(\\frac{1}{6}\\right) \\approx 9.5^\\circ'),
          insight('sin⁻¹, cos⁻¹ and tan⁻¹ are not new ratios. They are the same three functions run in the opposite direction: you hand them a ratio and they hand back the angle that would have produced it.'),
        ],
      },
      intuition: {
        title: 'Which direction, and which operation',
        blocks: [
          text('Every right-triangle problem is one of two questions, and naming which you are facing settles everything else.'),
          list([
            '**Angle known, side wanted.** Choose the ratio, then multiply or divide.',
            '**Two sides known, angle wanted.** Form the ratio, then apply the inverse.',
          ]),
          warning('The inverse is not a reciprocal. sin⁻¹(0.5) is 30°; 1/sin(0.5) is something else entirely. The superscript −1 here means "undo", as it does for an inverse function generally.', 'sin⁻¹ x is not 1/sin x'),
          text('When a side is wanted, the operation depends on where the unknown sits in the fraction:'),
          math('\\sin 40^\\circ = \\frac{x}{12} \\;\\Rightarrow\\; x = 12\\sin 40^\\circ \\qquad \\sin 40^\\circ = \\frac{12}{x} \\;\\Rightarrow\\; x = \\frac{12}{\\sin 40^\\circ}'),
          tip('Sine and cosine are always between 0 and 1, so multiplying by one shortens and dividing by one lengthens. The hypotenuse is the longest side of the triangle — if yours came out shorter than a leg, you multiplied where you should have divided.'),
          text('The last piece is vocabulary, and it is the piece that is misread. An **angle of elevation** is measured up from the horizontal; an **angle of depression** is measured down from the horizontal. Neither is measured from the vertical.'),
          insight('Standing on a cliff, your angle of depression to a boat equals the boat’s angle of elevation to you. The horizontal at the top and the sea are parallel, and the line of sight is a transversal — so they are alternate angles. That is why the angle can be moved to whichever corner of the triangle is more convenient.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'shape',
              shape: 'triangle',
              labels: { base: 'distance along the ground', height: 'height', hypotenuse: 'line of sight', angle: 'θ' },
            },
            'The standard picture. The angle of elevation sits between the horizontal and the line of sight — at the ground-level corner.',
          ),
          table(['You know', 'You want', 'What to do'], [
            ['Angle and hypotenuse', 'Opposite side', 'Multiply: O = H sin θ'],
            ['Angle and opposite', 'Hypotenuse', 'Divide: H = O ÷ sin θ'],
            ['Opposite and adjacent', 'The angle', 'θ = tan⁻¹(O ÷ A)'],
            ['Two sides, third side wanted', 'The third side', 'Pythagoras — no angle needed'],
          ]),
          warning('The last row matters. If no angle appears in the question at all, trigonometry is the long way round: a² + b² = c² is exact and needs no calculator table.', 'Not every triangle needs a ratio'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('From the top of a 46 m lighthouse, the angle of depression of a boat is 23°. How far is the boat from the foot of the lighthouse?'),
          steps([
            { label: 'Move the angle to the boat — alternate angles.', latex: '\\theta = 23^\\circ \\text{ at the boat, from the horizontal}' },
            { label: 'Relative to that angle, 46 m is opposite and the distance is adjacent.', latex: '\\tan 23^\\circ = \\frac{46}{d}' },
            { label: 'The unknown is underneath, so divide.', latex: 'd = \\frac{46}{\\tan 23^\\circ}' },
            { label: 'Evaluate.', latex: '\\approx 108.37\\text{ m}' },
          ]),
          text('Check the size. The angle is shallow — far from 45° — so the boat should be much further away than the lighthouse is tall, and 108 m against 46 m agrees. Had the answer come out at 19.5 m, that would be tan 23° used the wrong way round.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-applications', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-applications', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('trig-applications', 3, 'Elevation, depression, and deciding what to divide by what.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Two sides give an angle, through sin⁻¹, cos⁻¹ or tan⁻¹.',
            'sin⁻¹ undoes sine; it is not 1 ÷ sine.',
            'Unknown on top, multiply. Unknown underneath, divide.',
            'Elevation and depression are both measured from the horizontal, and the two are equal in the same picture.',
          ]),
        ],
      },
    },
  }),
];
